/**
 * Full-page navigation. Used by the auth-entry routes (login success, logout,
 * academy-login) where a hard reload — rather than a client-side transition —
 * matches the Ember behavior and fully resets app state. Isolated here so it
 * can be mocked in tests (`location` methods aren't spy-able in the browser
 * test runner).
 */
export function hardRedirect(target: string): void {
  window.location.assign(target);
}

/**
 * Constrain a redirect target to a same-origin, in-app path. Post-login the
 * `?origin=` query param (and the saved attempted URL) are attacker-
 * influenceable, and `hardRedirect` does a full `location.assign`, so a
 * cross-origin or protocol-relative URL would be an open redirect. Returns the
 * normalized internal path (`pathname + search + hash`) or `undefined` if it
 * isn't safe.
 *
 * Ember was implicitly safe here because it fed `origin` to the router's
 * `transitionTo`, which only matches internal routes; the hard redirect needs
 * this guard explicitly.
 *
 * The value may be a relative path (`/journeys`) OR a same-origin absolute URL
 * (`saveCurrentUrl` and the authenticated layout store `window.location.href`),
 * so resolve against the current origin first and reject only by origin — a
 * `startsWith('/')` check would drop legitimate same-origin absolute URLs.
 * Protocol-relative (`//evil.com`), backslash (`/\evil.com`), and non-http
 * schemes (`javascript:`) all resolve to a different origin (or `null`) and are
 * rejected by the origin comparison.
 */
export function toSafeInternalPath(value: string | undefined | null): string | undefined {
  if (value == null || value.trim() === '') {
    return undefined;
  }
  try {
    const url = new URL(value, window.location.origin);
    if (url.origin !== window.location.origin) {
      return undefined;
    }
    return url.pathname + url.search + url.hash;
  } catch {
    return undefined;
  }
}
